import moment from "moment";
import React from "react";
import QuestionShort from "./QuestionShort";
import QuestionMultiple from "./QuestionMultiple";
import QuestionScale from "./QuestionScale";
import { ProgressPictureAnswer } from "./QuestionProgressPictures";
import convert from "convert-units";
import { round } from "lodash";
import SummaryDailyStats from "../../metrics/check-in-summary/components/SummaryDailyStats";

const OnboardingSummary = ({ onboardingInfo, setOnboardingInfo, locals }) => {
  const { onboarding } = onboardingInfo;
  const { activeUser, query } = locals;
  const units = activeUser.units || "metric";
  const organizationCoach = query.organizationCoach || undefined;
  const organizationCoachParam = organizationCoach
    ? `&organizationCoach=${organizationCoach}`
    : "";

  const reversedAnswers = onboarding.answers.slice().reverse();

  const stepsArray = onboarding.answers.find(
    (answer) => answer.type === "steps"
  );
  const sleepArray = onboarding.answers.find(
    (answer) => answer.type === "sleep"
  );
  const hrvArray = onboarding.answers.find((answer) => answer.type === "hrv");

  const dailyStats = mergeHealthData(
    stepsArray?.answer?.value,
    sleepArray?.answer?.value,
    hrvArray?.answer?.value
  );

  return (
    <div className="onboarding-questionnaire">
      <div className="flex gap-4 mb-8">
        <button
          className="rd-back"
          onClick={() => {
            setOnboardingInfo(null);
          }}
        ></button>
        <div>
          <h4>Onboarding Questionnaire</h4>
          <p className="p5">
            Completed on{" "}
            {moment(onboardingInfo.utcDate, "YYYYMMDD").format("MMM D, YYYY")}
          </p>
        </div>
      </div>

      {reversedAnswers.map((answer) => (
        <DisplaySingleQuestion
          singleAnswer={answer}
          onboardingInfo={onboardingInfo}
          units={units}
          key={answer._id}
        />
      ))}

      <div id="check-in-summary">
        <SummaryDailyStats
          athlete={locals.athlete}
          date={moment(onboardingInfo.utcDate, "YYYYMMDD").format("YYYY-MM-DD")}
          organizationCoachParam={organizationCoachParam}
          dailyStats={dailyStats}
        />
      </div>
    </div>
  );
};

const DisplaySingleQuestion = ({ singleAnswer, onboardingInfo, units }) => {
  const { type, answer, questionBody, questionTitle } = singleAnswer;

  // Cuz we are already showing these without the need for a question
  if (type === "sleep" || type === "steps" || type === "hrv") return null;

  if (type === "measurement") {
    const atLeastOneMeasurement = Object.values(answer.value).some(
      (value) => Number(value) !== 0
    );

    if (!atLeastOneMeasurement) return null;
  }

  const questionDisplayTitle = getQuestionTitle(type, questionTitle);

  const questionDisplayBody = getQuestionBody(
    type,
    questionBody,
    answer,
    onboardingInfo,
    units
  );

  return (
    <div className={"questionnaire-question"}>
      <div className="question-header">
        <h4>{questionDisplayTitle}</h4>
      </div>
      <div className="question-body">{questionDisplayBody}</div>
    </div>
  );
};

const getQuestionTitle = (type, questionTile) => {
  switch (type) {
    case "short":
    case "multiple":
    case "checkbox":
    case "scale":
    case "measurement":
      return questionTile;
    case "bodyweight":
      return "Bodyweight";
    case "nutrition":
      return "Nutrition Summary";
    case "progress":
      return "Progress Photos";
  }
};

const getQuestionBody = (type, body, answer, onboardingInfo, units) => {
  if (!answer && type !== "progress") {
    return <div>Error displaying the question</div>;
  }

  switch (type) {
    case "short":
      return <QuestionShort description={answer.value} />;
    case "multiple":
    case "checkbox":
      return (
        <QuestionMultiple
          type={body.type}
          options={body.options}
          answers={answer.value}
        />
      );
    case "scale":
      return (
        <QuestionScale
          labelLeft={body.labelLeft}
          labelRight={body.labelRight}
          answer={answer.value}
        />
      );
    case "bodyweight":
      const bodyweight = answer.value.bodyweight;
      const isPounds = answer.value.isPounds;
      const label = units === "metric" ? "Kg" : "Lbs";
      const isMetric = units === "metric";

      const displayWeight = isPounds
        ? isMetric
          ? convert(bodyweight).from("lb").to("kg")
          : bodyweight
        : isMetric
        ? bodyweight
        : convert(bodyweight).from("kg").to("lb");

      return (
        <QuestionShort description={`${round(displayWeight, 2)} ${label}`} />
      );
    case "progress":
      return <ProgressPictureAnswer dailyStats={[onboardingInfo]} />;
    case "nutrition":
      const nutritionData = answer.value;

      return (
        <section
          role="table-wrapper"
          className="nutrition-table"
          data-scrollable
        >
          <table role="report-table">
            <thead>
              <tr>
                <th>Date</th>
                <th className="protein">
                  Protein
                  <br />
                  <p className="st1">(g)</p>
                </th>
                <th className="carbs">
                  Carbs
                  <br />
                  <p className="st1">(g)</p>
                </th>
                <th className="fat">
                  Fat
                  <br />
                  <p className="st1">(g)</p>
                </th>
                <th>
                  Fiber
                  <br />
                  <p className="st1">(g)</p>
                </th>
                <th>
                  kCal
                  <br />
                  <p className="st1"></p>
                </th>
              </tr>
            </thead>

            <tbody>
              {nutritionData.map((data) => (
                <tr key={`onboarding-nutrition-${data.utcDate}`}>
                  <td>
                    {moment(data.utcDate, "YYYYMMDD").format("MMM D, YYYY")}
                  </td>
                  <td>{round(data.proteins, 0) || "-"}</td>
                  <td>{round(data.carbs, 0) || "-"}</td>
                  <td>{round(data.fats, 0) || "-"}</td>
                  <td>{round(data.fibers, 0) || "-"}</td>
                  <td>
                    {round(
                      data.proteins * 4 + data.carbs * 4 + data.fats * 9,
                      0
                    ) || "-"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      );

    case "measurement":
      const { value: measurements } = answer;
      const unitLabel = units === "metric" ? "cm" : "inch";

      const tableBody = Object.entries(measurements).map(
        ([partName, value]) => {
          const measure = measures(partName);
          const convertedValue = units === "metric" ? value : value / 2.54;
          return {
            text: measure?.text || partName,
            value: round(convertedValue, 2),
          };
        }
      );

      return (
        <section
          role="table-wrapper"
          className="nutrition-table w-1/2"
          data-scrollable
        >
          <table role="report-table">
            <thead>
              <tr>
                <th>Part</th>
                <th>Measurement ({unitLabel || "cm"})</th>
              </tr>
            </thead>

            <tbody>
              {tableBody.map(({ text, value }) => (
                <tr key={`${text}-${value}-measurements`}>
                  <td>{text}</td>
                  <td>{value}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      );
    default:
      return <div>Error displaying the question</div>;
  }
};

function mergeHealthData(stepsArray, sleepArray, hrvArray) {
  const mergedData = {};

  // Merge each dataset
  addData(stepsArray, "steps", mergedData);
  addData(sleepArray, "sleep", mergedData);
  addData(hrvArray, "hrv", mergedData);

  // Convert mergedData back to an array
  return Object.values(mergedData);
}

// Helper function to add data to mergedData
function addData(array, key, mergedData) {
  if (!array) return; // Skip undefined arrays

  array.forEach((item) => {
    const { utcDate, date } = item;
    if (!mergedData[utcDate]) {
      mergedData[utcDate] = { utcDate, date };
    }
    mergedData[utcDate][key] = item[key] || 0;
  });
}

const measures = (part) => {
  const partNames = {
    neck: {
      text: "Neck",
    },
    chest: {
      text: "Chest",
    },
    leftArm: {
      text: "Left Arm",
    },
    rightArm: {
      text: "Right Arm",
    },
    waist: {
      text: "Waist",
    },
    hips: {
      text: "Hips",
    },
    leftThigh: {
      text: "Left Thigh",
    },
    rightThigh: {
      text: "Right Thigh",
    },
    leftCalf: {
      text: "Left Calf",
    },
    rightCalf: {
      text: "Right Calf",
    },
  };

  return partNames[part];
};

export default OnboardingSummary;
